import { Service, inject } from "@angular/core";
import { RolesEnum } from "app/shared/enums/roles.enum";
import { AgencyDto } from "app/shared/models/agency.model";
import { HttpBasicRequest } from "app/shared/models/http-basic-request.model";
import { PagedResponse } from "app/shared/models/paged-response.model";
import { BasicHttpClientService } from "app/shared/services/basic-http-client.service";
import { environment } from "environments/environment";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { map, skip } from "rxjs/operators";

import { CustomerFullDTO } from "../../models/customer/customer.model";
import { AgentDTO } from "../../models/users/agent.models";
import { RoleDTO, RoleVM } from "../../models/users/role.models";
import {
  CreateOrEditUserDTO,
  UserDTO,
  UserVM,
} from "../../models/users/user.model";
import { UserMapper } from "../../utilities/user-mapper";
import { SessionStore } from "app/shared/state/session.store";
import { ShopContextStore } from "app/shared/state/shop-context.store";

@Service()
export class UsersService {
  private _httpClient = inject(BasicHttpClientService);

  private _baseUrl: string = environment.urls.apiUrl.url;
  private _data = new BehaviorSubject<{
    data: AgencyDto | CustomerFullDTO;
    type: "customer" | "agency";
  }>(null);
  get currentData(): Observable<{
    data: AgencyDto | CustomerFullDTO;
    type: "customer" | "agency";
  }> {
    return this._data.asObservable();
  }
  get currentDataValue(): {
    data: AgencyDto | CustomerFullDTO;
    type: "customer" | "agency";
  } {
    return this._data.value;
  }
  setCurrentData(data: {
    data: AgencyDto | CustomerFullDTO;
    type: "customer" | "agency";
  }) {
    this._data.next(data);
  }

  private _session = inject(SessionStore);
  private _shopContext = inject(ShopContextStore);

  getAllUsers(
    loaderId?: string,
    skipCount?: number,
    maxResultCount?: number,
    customerId?: number,
    searchString?: string,
    roleNames?: string[],
    storeId?: number,
    email?: string,
  ): Observable<PagedResponse<UserVM>> {
    const params = {};
    if (skipCount) {
      params["skipCount"] = skipCount?.toString();
    }
    if (maxResultCount) {
      params["maxResultCount"] = maxResultCount?.toString();
    }
    if (customerId) {
      params["customerId"] = customerId;
    }
    if (searchString) {
      params["searchString"] = searchString;
    }
    if (roleNames?.length) {
      params["roleNames"] = roleNames;
    }
    if (storeId) {
      params["storeId"] = storeId;
    }
    if (email) {
      params["email"] = email;
    }
    params["ignoreStatus"] = "true";

    return this._httpClient
      .get<
        PagedResponse<UserDTO>
      >(this._baseUrl + "/users", { params }, loaderId)
      .pipe(
        map((data) => {
          return new PagedResponse<UserVM>({
            items: data.items.map((x) =>
              UserMapper.UserVM(
                x,
                this._shopContext.shops(),
                this._shopContext.currentShop().id,
              ),
            ),
            totalCount: data.totalCount,
          });
        }),
      );
  }

  checkExistingUser(email: string, loaderId?: string): Observable<boolean> {
    return this.getUserByMail(email, loaderId).pipe(map((data) => !!data));
  }

  getUserByMail(email: string, loaderId?: string): Observable<UserVM> {
    const params = {
      skipCount: 0,
      maxResultCount: 0,
      email: email,
      ignoreStatus: "true",
    };
    return this._httpClient
      .get<
        PagedResponse<UserDTO>
      >(this._baseUrl + "/users", { params }, loaderId)
      .pipe(
        map((data) =>
          data?.items[0] ? UserMapper.UserVM(data?.items[0], [], null) : null,
        ),
      );
  }

  getAgents(
    loaderId?: string,
    skipCount?: number,
    maxResultCount?: number,
    agencyId?: number,
    searchString?: string,
  ): Observable<PagedResponse<UserVM>> {
    const params = {};
    if (skipCount >= 0 && skipCount != null) params["skipCount"] = skipCount;
    if (maxResultCount >= 0 && maxResultCount != null)
      params["maxResultCount"] = maxResultCount;
    if (agencyId) params["agencyId"] = agencyId;
    if (searchString) params["searchString"] = searchString;
    return this._httpClient
      .get<
        PagedResponse<AgentDTO>
      >(this._baseUrl + "/agencies/agents", { params }, loaderId)
      .pipe(
        map((data) => {
          return new PagedResponse<UserVM>({
            items: data.items.map((x) =>
              UserMapper.UserVM(
                x.user,
                this._shopContext.shops(),
                this._shopContext.currentShop().id,
              ),
            ),
            totalCount: data.totalCount,
          });
        }),
      );
  }

  getUserDetails(id: number, loaderId: string): Observable<UserVM> {
    const url = this._baseUrl + "/users/" + id.toString();
    return this._httpClient
      .get<UserDTO>(url, null, loaderId)
      .pipe(
        map((x) =>
          UserMapper.UserVM(
            x,
            this._shopContext.shops(),
            this._shopContext.currentShop().id,
          ),
        ),
      );
  }

  getUserName(id: number, loaderId: string): Observable<UserDTO> {
    try {
      return this._httpClient.get<UserDTO>(
        `${this._baseUrl}/users/${id}/info`,
        null,
        loaderId,
      );
    } catch {
      console.error("No user found to show last modefier user name");
    }
  }

  getRoles(
    skipCount: number,
    maxResultCount: number,
    search: string,
    loaderId: string,
  ): Observable<PagedResponse<RoleVM>> {
    const url = this._baseUrl + "/roles";
    const params = {
      skipCount,
      maxResultCount,
      searchString: search || "",
    };
    return this._httpClient
      .get<PagedResponse<RoleDTO>>(url, { params }, loaderId)
      .pipe(
        map(
          (x) =>
            new PagedResponse<RoleVM>({
              items: x.items
                // .filter(x => x.id !== RolesEnum.Service)
                .filter(
                  (x) =>
                    x.id !== RolesEnum.Service && x.id !== RolesEnum.StoreUser,
                ) //TODO:da completare gestione di storeuser
                .map((y) =>
                  UserMapper.RoleVM(
                    y,
                    null,
                    this._session.userInfo()?.mainRole,
                  ),
                ),
              totalCount:
                x.totalCount -
                x.items.filter((x) => x.id === RolesEnum.Service).length,
            }),
        ),
      );
  }

  removeUser(userId: number, loaderId?: string) {
    return this._httpClient.delete(
      this._baseUrl + `/users/${userId}}`,
      {},
      loaderId,
    );
  }

  createUser(currentUser: UserVM, loaderId: string, blockInvitation?: boolean) {
    const result = new Subject();
    const userRequest = new CreateOrEditUserDTO({
      email: currentUser.email,
      externalId: null,
      name: currentUser.name,
      tenantId: this._session.userInfo().tenant.id.toString(),
    });

    this._httpClient
      .post(this._baseUrl + "/users", userRequest, null, loaderId)
      .subscribe((userResp: UserDTO) => {
        const userId = userResp.id;
        const userRolesReq: HttpBasicRequest[] = currentUser.roles.map(
          (x) =>
            new HttpBasicRequest({
              url: `${this._baseUrl}/roles/${RolesEnum[x]}/user/${userId}`,
              method: "post",
            }),
        );

        const agencyCustomerAssociation: HttpBasicRequest[] = [];

        if (
          currentUser.roles.some(
            (x) =>
              x === RolesEnum[RolesEnum.CustomerOwner] ||
              x === RolesEnum[RolesEnum.Customer] ||
              x === RolesEnum[RolesEnum.CustomerGuest],
          )
        ) {
          agencyCustomerAssociation.push({
            url: `${this._baseUrl}/customers/${currentUser.customer.id}/user/${userId}`,
            method: "post",
          });
        }
        if (
          currentUser.roles.some(
            (x) =>
              x === RolesEnum[RolesEnum.AgencyOwner] ||
              x === RolesEnum[RolesEnum.Agent],
          )
        ) {
          // const agentReq: AgentCreateDTO = {
          //   agencyId: currentUser.agency.id,
          //   userId: userId,
          //   vatNumber: null,
          //   externalId: null
          // };
          agencyCustomerAssociation.push({
            url: `${this._baseUrl}/agencies/${currentUser.agency.id}/agents/${userId}`,
            method: "post",
          });
        }
        if (
          currentUser.roles.some(
            (x) =>
              x === RolesEnum[RolesEnum.StoreOwner] ||
              x === RolesEnum[RolesEnum.StoreUser] ||
              x === RolesEnum[RolesEnum.Designer],
          ) &&
          currentUser.storeIds?.length
        ) {
          currentUser.storeIds.forEach((x) =>
            agencyCustomerAssociation.push({
              url: `${this._baseUrl}/stores/${x}/user/${userId}`,
              method: "post",
            }),
          );
        }
        this._httpClient.forkJoin(userRolesReq, loaderId).subscribe(() => {
          if (agencyCustomerAssociation.length > 0) {
            this._httpClient
              .forkJoin(agencyCustomerAssociation, loaderId)
              .subscribe(() => {
                !blockInvitation
                  ? this.sendEmailRegistrationInvite(
                      userResp.id,
                      this._shopContext.currentShop().id,
                      loaderId,
                    ).subscribe(() => {
                      result.next(undefined);
                    })
                  : result.next(undefined);
              });
          } else {
            !blockInvitation
              ? this.sendEmailRegistrationInvite(
                  userResp.id,
                  this._shopContext.currentShop().id,
                  loaderId,
                ).subscribe(() => {
                  result.next(undefined);
                })
              : result.next(undefined);
          }
        });
      });

    return result.asObservable();
  }

  editUser(currentUser: UserVM, cachedUser: UserVM, loaderId: string) {
    const result = new Subject();
    const userRequest = new CreateOrEditUserDTO({
      id: currentUser.id,
      name: currentUser.name,
      culture: currentUser.culture,
      externalId: currentUser.externalId,
    });

    const newRoles = currentUser.roles
      .filter((x) => !cachedUser.roles.some((y) => y === x))
      .map((x) => x.replace(" ", ""));
    const toDeleteRoles = cachedUser.roles
      .filter((x) => !currentUser.roles.some((y) => y === x))
      .map((x) => x.replace(" ", ""));

    let userRoleReq: HttpBasicRequest[] = [];

    if (
      !!cachedUser.agency?.id &&
      (!currentUser.agency || currentUser.agency.id != cachedUser.agency.id)
    )
      userRoleReq.push(
        new HttpBasicRequest({
          url: `${this._baseUrl}/agencies/${cachedUser.agency.id}/agents/${cachedUser.id}`,
          method: "delete",
        }),
      );

    userRoleReq = userRoleReq.concat(
      toDeleteRoles.map(
        (x) =>
          new HttpBasicRequest({
            url: `${this._baseUrl}/roles/${RolesEnum[x]}/user/${currentUser.id}`,
            method: "delete",
          }),
      ),
    );

    const newRolesReq = newRoles.map(
      (x) =>
        new HttpBasicRequest({
          url: `${this._baseUrl}/roles/${RolesEnum[x]}/user/${currentUser.id}`,
          method: "post",
        }),
    );

    // if (currentUser.isNew) { newRolesReq.push(new HttpBasicRequest({ url: `${this.baseUrl}/users/activate?userIdToActivate=${currentUser.id}`, method: 'post' })); }

    const agencyCustomerAssociation: HttpBasicRequest[] = [];
    if (
      !!cachedUser.customer &&
      (!currentUser.customer ||
        currentUser.customer.id != cachedUser.customer.id)
    ) {
      agencyCustomerAssociation.push({
        url: `${this._baseUrl}/customers/${cachedUser.customer.id}/user/${currentUser.id}`,
        method: "delete",
      });
    }

    if (
      !!currentUser.customer &&
      (!cachedUser.customer ||
        currentUser.customer.id != cachedUser.customer.id)
    ) {
      agencyCustomerAssociation.push({
        url: `${this._baseUrl}/customers/${currentUser.customer.id}/user/${currentUser.id}`,
        method: "post",
      });
    }

    if (
      currentUser.agency &&
      (!cachedUser.agency || currentUser.agency.id !== cachedUser.agency.id)
    ) {
      // const agentReq: AgentCreateDTO = {
      //   agencyId: currentUser.agency.id,
      //   userId: currentUser.id,
      //   vatNumber: null,
      //   externalId: null
      // };
      agencyCustomerAssociation.push({
        url: `${this._baseUrl}/agencies/${currentUser.agency.id}/agents/${currentUser.id}`,
        method: "post",
      });
    }

    cachedUser.storeIds
      ?.filter((storeId) =>
        this._shopContext.shops().some((shopStore) => shopStore.id === storeId),
      )
      ?.filter((x) => !currentUser.storeIds?.some((y) => y === x))
      .forEach((x) =>
        agencyCustomerAssociation.push({
          url: `${this._baseUrl}/stores/${x}/user/${cachedUser.id}`,
          method: "delete",
        }),
      );
    currentUser.storeIds
      ?.filter((x) => !cachedUser.storeIds.some((y) => y === x))
      .forEach((x) =>
        agencyCustomerAssociation.push({
          url: `${this._baseUrl}/stores/${x}/user/${currentUser.id}`,
          method: "post",
        }),
      );
    this._httpClient
      .put(
        this._baseUrl + "/users/" + currentUser.id,
        userRequest,
        null,
        loaderId,
      )
      .subscribe(() => {
        if (userRoleReq.length) {
          this._httpClient.forkJoin(userRoleReq).subscribe(() => {
            newRolesReq.length
              ? this._httpClient
                  .forkJoin(newRolesReq)
                  .subscribe(() =>
                    agencyCustomerAssociation.length
                      ? this._httpClient
                          .forkJoin(agencyCustomerAssociation)
                          .subscribe(() => result.next(undefined))
                      : result.next(undefined),
                  )
              : agencyCustomerAssociation.length
                ? this._httpClient
                    .forkJoin(agencyCustomerAssociation)
                    .subscribe(() => result.next(undefined))
                : result.next(undefined);
          });
        } else if (newRolesReq.length) {
          this._httpClient
            .forkJoin(newRolesReq)
            .subscribe(() =>
              agencyCustomerAssociation.length
                ? this._httpClient
                    .forkJoin(agencyCustomerAssociation)
                    .subscribe(() => result.next(undefined))
                : result.next(undefined),
            );
        } else if (agencyCustomerAssociation.length) {
          this._httpClient
            .forkJoin(agencyCustomerAssociation)
            .subscribe(() => result.next(undefined));
        } else {
          result.next(undefined);
        }
      });
    return result.asObservable();
  }

  activateUser(userId: string, loaderId: string) {
    return this._httpClient.post(
      this._baseUrl + "/users/activate?userIdToActivate=" + userId,
      null,
      null,
      loaderId,
    );
  }

  deactivateUser(userId: string, loaderId: string) {
    return this._httpClient.post(
      this._baseUrl + "/users/deactivate?userIdToDeactivate=" + userId,
      null,
      null,
      loaderId,
    );
  }

  sendEmailRegistrationInvite(
    userId: number,
    storeId: number,
    loaderId: string,
  ) {
    return this._httpClient.post(
      this._baseUrl + `/users/${userId}/invitation/${storeId}/notify`,
      null,
      null,
      loaderId,
    );
  }
}
